import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Skeleton } from "../../../ui/skeleton";
import {
  Bell,
  Calendar,
  Trash2,
  ExternalLink,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Lightbulb,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getMyReminders,
  cancelReminder,
  CheckpointReminder,
} from "../../../../api/checkpointReminders";
import useProcess from "../../../../hooks/useProcess";
import { CreateReminderModal } from "../../../notifications/CreateReminderModal";

interface UpcomingRemindersProps {
  onCountChange?: (count: number) => void;
}

const UpcomingReminders: React.FC<UpcomingRemindersProps> = ({ onCountChange }) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const { startProcess, resumeProcess } = useProcess();
  const [reminders, setReminders] = useState<CheckpointReminder[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const response = await getMyReminders(retryFetch, {
        status: "pending",
        includeArchived: false,
      });

      // Sort by scheduled date/time
      const sorted = response.reminders.sort((a, b) => {
        return new Date(a.scheduledDateTime).getTime() - new Date(b.scheduledDateTime).getTime();
      });

      setReminders(sorted);
      onCountChange?.(sorted.length);
    } catch (error) {
      console.error("Failed to fetch reminders:", error);
      toast.error(t("failed_to_load_reminders"));
      setReminders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReminder = async (reminderId: string) => {
    try {
      await cancelReminder(retryFetch, reminderId);
      toast.success(t("reminder_cancelled"));

      const newReminders = reminders.filter(r => r._id !== reminderId);
      setReminders(newReminders);
      onCountChange?.(newReminders.length);
    } catch (error) {
      console.error("Failed to cancel reminder:", error);
      toast.error(t("failed_to_cancel_reminder"));
    }
  };

  const openReminder = async (reminder: CheckpointReminder) => {
    if (reminder.reminderAction === "resume_engagement" && reminder.fiProcess) {
      // Ensure fiProcess is a string (handle case where it might be an object with _id)
      const processId = typeof reminder.fiProcess === 'string'
        ? reminder.fiProcess
        : (reminder.fiProcess as any)?._id || reminder.fiProcess;

      // Use resumeProcess to properly resume the engagement
      await resumeProcess({
        processId,
        from: location,
        forward: location,
        onError: () => {
          toast.error(t("failed_to_open_engagement"));
        },
      });
    } else if (reminder.reminderAction === "start_new_engagement" && reminder.fiPrescription) {
      // Ensure fiPrescription is a string (handle case where it might be an object with _id)
      const prescriptionId = typeof reminder.fiPrescription === 'string'
        ? reminder.fiPrescription
        : (reminder.fiPrescription as any)?._id || reminder.fiPrescription;

      // Use startProcess to properly create a new process and navigate
      await startProcess({
        prescriptionId,
        from: location,
        forward: location,
        onError: () => {
          toast.error(t("failed_to_start_engagement"));
        },
      });
    }
  };

  const getReminderActionBadge = (action: string) => {
    switch (action) {
      case "resume_engagement":
        return <Badge variant="default">{t("resume_engagement")}</Badge>;
      case "start_new_engagement":
        return <Badge variant="secondary">{t("start_new")}</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const getFrequencyBadge = (frequency: string) => {
    switch (frequency) {
      case "once":
        return <Badge variant="outline">{t("once")}</Badge>;
      case "daily":
        return <Badge variant="outline">{t("daily")}</Badge>;
      case "weekly":
        return <Badge variant="outline">{t("weekly")}</Badge>;
      case "biweekly":
        return <Badge variant="outline">{t("biweekly")}</Badge>;
      case "monthly":
        return <Badge variant="outline">{t("monthly")}</Badge>;
      default:
        return <Badge variant="outline">{frequency}</Badge>;
    }
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffDays < 0) {
      return <span className="text-destructive">{t("overdue")}</span>;
    } else if (diffDays === 0) {
      if (diffHours === 0) {
        return <span className="text-amber-500">{t("within_hour")}</span>;
      }
      return <span className="text-amber-500">{t("today")}</span>;
    } else if (diffDays === 1) {
      return <span className="text-primary">{t("tomorrow")}</span>;
    } else if (diffDays < 7) {
      return <span>{t("in_x_days", { count: diffDays })}</span>;
    }

    return date.toLocaleDateString();
  };

  const getReminderTypeBadge = (type: string) => {
    if (type === "author_default") {
      return (
        <Badge variant="secondary" className="text-xs">
          <Clock className="h-3 w-3 mr-1" />
          {t("default")}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-xs">
        {t("custom")}
      </Badge>
    );
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t("upcoming_reminders")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("scheduled_engagement_reminders")}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setIsCreateModalOpen(true)}
          className="h-8 gap-1.5"
        >
          <Plus className="w-4 h-4" />
          {t("create_reminder")}
        </Button>
      </div>

      {loading ? (
        <div className="flex-1 border rounded-lg overflow-hidden bg-card shadow-sm">
          <div className="p-1">
            {/* Table header skeleton */}
            <div className="flex items-center gap-4 px-4 py-3 border-b bg-muted/50">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20 hidden md:block" />
              <Skeleton className="h-4 w-16 hidden lg:block" />
              <Skeleton className="h-4 w-16 hidden lg:block" />
              <div className="flex-1" />
              <Skeleton className="h-4 w-12" />
            </div>
            {/* Row skeletons */}
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex items-center gap-4 px-4 py-4 border-b last:border-0">
                <div className="flex items-center gap-2 flex-1">
                  <Skeleton className="h-10 w-10 rounded-lg flex-shrink-0" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <div className="hidden md:flex flex-col gap-1">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-3 w-12" />
                </div>
                <Skeleton className="h-5 w-20 rounded-full hidden lg:block" />
                <Skeleton className="h-5 w-14 rounded-full hidden lg:block" />
                <div className="flex gap-1">
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : reminders.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <Bell className="h-16 w-16 text-purple-500 mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">{t("no_upcoming_reminders")}</h3>
          <p className="text-muted-foreground max-w-md">
            {t("reminders_help_text")}
          </p>
        </div>
      ) : (
        <div className="flex-1 border rounded-lg overflow-hidden bg-card shadow-sm">
          <div className="overflow-auto h-full">
            <Table>
              <TableHeader className="sticky top-0 bg-muted/50 z-10 border-b">
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t("activity")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("scheduled")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("action")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("frequency")}</TableHead>
                  <TableHead className="hidden xl:table-cell">{t("type")}</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reminders.map((reminder) => (
                  <TableRow key={reminder._id} className="hover:bg-muted/30 transition-colors">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                          <Bell className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="font-medium truncate max-w-md">{reminder.activityTitle}</span>
                          <div className="flex items-center gap-2 flex-wrap">
                            {reminder.emailContent?.actionItems && reminder.emailContent.actionItems.length > 0 && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <CheckCircle2 className="h-3 w-3 text-green-600" />
                                <span>{reminder.emailContent.actionItems.length} {t("action_items")}</span>
                              </div>
                            )}
                            {reminder.emailContent?.insights && reminder.emailContent.insights.length > 0 && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Lightbulb className="h-3 w-3 text-amber-600" />
                                <span>{reminder.emailContent.insights.length} {t("insights")}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">{formatDateTime(reminder.scheduledDateTime)}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(reminder.scheduledDateTime).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {getReminderActionBadge(reminder.reminderAction)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {getFrequencyBadge(reminder.frequency)}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {getReminderTypeBadge(reminder.reminderType)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-purple-50 hover:text-purple-600"
                          onClick={() => openReminder(reminder)}
                          title={t("open_engagement")}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-red-50"
                          onClick={() => handleCancelReminder(reminder._id)}
                          title={t("cancel_reminder")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <CreateReminderModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onReminderCreated={fetchReminders}
      />
    </div>
  );
};

export default UpcomingReminders;
